import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { VideoUploader } from "../components/VideoUploader";
import { AIVideoUploader } from "../components/AIVideoUploader";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, Eye, MessageSquare, ArrowLeft, Play, BadgeCheck } from "lucide-react";
import { useAuth } from "../lib/auth";

export default function DebateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [debate, setDebate] = useState(null);
  const [tab, setTab] = useState("support");
  const [replyMode, setReplyMode] = useState(null); // 'support' | 'oppose' | null
  const [replyProcessed, setReplyProcessed] = useState(null); // { video_url, thumbnail_url, description, transcript }
  const [replyText, setReplyText] = useState("");
  const [posting, setPosting] = useState(false);
  const [selectedReply, setSelectedReply] = useState(null);

  const load = () => api.get(`/debates/${id}`).then((r) => setDebate(r.data));
  useEffect(() => { load(); }, [id]);

  // Auto-open reply form when arriving from a card with ?reply=support|oppose
  useEffect(() => {
    const r = searchParams.get("reply");
    if (r === "support" || r === "oppose") {
      setReplyMode(r);
      // Scroll to composer once it mounts
      setTimeout(() => {
        document.getElementById("reply-composer")?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 400);
      // Strip query param so refresh doesn't re-open
      searchParams.delete("reply");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!debate) return <div className="p-10 text-muted-foreground">Loading debate…</div>;

  const currentVideo = selectedReply ? selectedReply.video_url : debate.video_url;
  const currentTitle = selectedReply ? `Reply · @${selectedReply.author?.username}` : debate.title;
  const supportReplies = debate.replies.filter((r) => r.position === "support");
  const opposeReplies = debate.replies.filter((r) => r.position === "oppose");

  const supportCount = debate.support_count || 0;
  const opposeCount = debate.oppose_count || 0;
  const totalCount = supportCount + opposeCount;
  const supportPct = totalCount > 0 ? Math.round((supportCount / totalCount) * 100) : 50;
  const opposePct = totalCount > 0 ? 100 - supportPct : 50;

  const openSupport = () => {
    if (!debate.allow_replies) { toast.info("Replies disabled by the creator."); return; }
    if (user?.verification_status !== "verified") {
      toast.info("Verify your identity to reply."); navigate("/verify"); return;
    }
    if (debate.my_position === "oppose") {
      toast.error("You've already replied in oppose. You can only add more oppose replies on this debate.");
      return;
    }
    setReplyMode("support");
    setTimeout(() => document.getElementById("reply-composer")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };
  const openOppose = () => {
    if (!debate.allow_replies) { toast.info("Replies disabled by the creator."); return; }
    if (user?.verification_status !== "verified") {
      toast.info("Verify your identity to reply."); navigate("/verify"); return;
    }
    if (debate.my_position === "support") {
      toast.error("You've already replied in support. You can only add more support replies on this debate.");
      return;
    }
    setReplyMode("oppose");
    setTimeout(() => document.getElementById("reply-composer")?.scrollIntoView({ behavior: "smooth", block: "center" }), 100);
  };

  const submitReply = async () => {
    if (!replyProcessed?.video_url) { toast.error("Upload a video first"); return; }
    setPosting(true);
    try {
      await api.post(`/debates/${id}/reply`, {
        debate_id: id,
        parent_reply_id: null,
        video_url: replyProcessed.video_url,
        thumbnail_url: replyProcessed.thumbnail_url,
        position: replyMode,
        text: replyText || replyProcessed.description || "",
      });
      toast.success("Reply posted");
      setReplyMode(null); setReplyProcessed(null); setReplyText("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to reply");
    } finally { setPosting(false); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6">
      <button
        onClick={() => navigate(-1)}
        className="text-xs font-semibold text-muted-foreground mb-6 flex items-center gap-2 hover:text-foreground tracking-wider"
        data-testid="debate-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> BACK
      </button>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Main video */}
        <div className="lg:col-span-8">
          <div className="bg-black rounded-2xl overflow-hidden border border-border">
            {currentVideo ? (
              <video
                key={currentVideo}
                src={fileUrl(currentVideo)}
                controls
                autoPlay
                className="w-full aspect-video"
                data-testid="debate-main-video"
              />
            ) : (
              <div className="w-full aspect-video flex items-center justify-center">
                <Play className="w-12 h-12 text-muted-foreground" />
              </div>
            )}
          </div>

          <div className="mt-6 pb-6 border-b border-border">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <div className={`text-white text-[10px] font-semibold tracking-wider px-3 py-1.5 rounded-full ${debate.position === "support" ? "bg-[hsl(var(--support))]" : "bg-[hsl(var(--oppose))]"}`}>
                {debate.position === "support" ? "SUPPORTS · TOPIC" : "OPPOSES · TOPIC"}
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" /> {debate.views}
              </div>
            </div>
            <h1 className="font-serif text-3xl lg:text-5xl leading-tight mb-4">{currentTitle}</h1>
            {debate.description && !selectedReply && (
              <p className="text-muted-foreground leading-relaxed mb-5 text-base">{debate.description}</p>
            )}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <Link to={`/profile/${debate.author?.username}`} className="flex items-center gap-3 hover:opacity-80" data-testid="debate-author-link">
                <div className="w-11 h-11 rounded-full bg-[hsl(var(--primary-soft))] border border-border flex items-center justify-center font-mono overflow-hidden">
                  {debate.author?.profile_photo ? (
                    <img src={fileUrl(debate.author.profile_photo)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-bold text-[hsl(var(--primary))]">{debate.author?.username?.[0]?.toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <div className="text-sm font-semibold flex items-center gap-1">
                    @{debate.author?.username}
                    {debate.author?.verification_status === "verified" && (
                      <BadgeCheck className="w-4 h-4 fill-[hsl(var(--primary))] text-white" />
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">{debate.author?.full_name}</div>
                </div>
              </Link>
              {/* Like / Dislike buttons now open the SUPPORT / OPPOSE reply form */}
              <div className="flex gap-2">
                <button
                  onClick={openSupport}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-border hover:border-[hsl(var(--support))] hover:bg-[hsl(var(--support-soft))] hover:text-[hsl(var(--support))] transition-colors text-sm font-semibold"
                  data-testid="debate-like-btn"
                  aria-label="Reply in support"
                >
                  <ThumbsUp className="w-5 h-5" strokeWidth={1.75} /> {supportCount}
                </button>
                <button
                  onClick={openOppose}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-border hover:border-[hsl(var(--oppose))] hover:bg-[hsl(var(--oppose-soft))] hover:text-[hsl(var(--oppose))] transition-colors text-sm font-semibold"
                  data-testid="debate-dislike-btn"
                  aria-label="Reply in opposition"
                >
                  <ThumbsDown className="w-5 h-5" strokeWidth={1.75} /> {opposeCount}
                </button>
              </div>
            </div>
          </div>

          {/* Reply composer (opens via like/dislike or ?reply= query) */}
          <div id="reply-composer" className="mt-6">
            {replyMode ? (
              <div className={`rounded-2xl border-2 p-6 ${replyMode === "support" ? "border-[hsl(var(--support))] bg-[hsl(var(--support-soft))]" : "border-[hsl(var(--oppose))] bg-[hsl(var(--oppose-soft))]"}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className={`text-xs font-semibold tracking-wider ${replyMode === "support" ? "text-[hsl(var(--support))]" : "text-[hsl(var(--oppose))]"}`}>
                    REPLY · {replyMode.toUpperCase()}
                  </div>
                  <button
                    onClick={() => { setReplyMode(null); setReplyProcessed(null); setReplyText(""); }}
                    className="text-xs font-semibold text-muted-foreground hover:text-foreground"
                    data-testid="reply-cancel"
                  >
                    Cancel
                  </button>
                </div>
                <AIVideoUploader title={`Reply · ${debate.title}`} onProcessed={setReplyProcessed} testId="reply-uploader" compact />
                <Textarea
                  className="rounded-xl mt-4 bg-card border-border"
                  placeholder="Optional context (leave blank to use the AI-generated description)…"
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  data-testid="reply-text-input"
                />
                <Button
                  onClick={submitReply}
                  disabled={posting || !replyProcessed?.video_url}
                  className={`rounded-full mt-4 text-white ${replyMode === "support" ? "bg-[hsl(var(--support))] hover:bg-[hsl(var(--support))]/90" : "bg-[hsl(var(--oppose))] hover:bg-[hsl(var(--oppose))]/90"}`}
                  data-testid="reply-submit"
                >
                  Post {replyMode} reply
                </Button>
              </div>
            ) : !debate.allow_replies ? (
              <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="replies-disabled-notice">
                <MessageSquare className="w-6 h-6 mx-auto mb-2 opacity-60" /> Replies disabled by the creator
              </div>
            ) : (
              <p className="text-xs text-muted-foreground text-center">
                Click the <ThumbsUp className="w-3 h-3 inline mx-1" /> or <ThumbsDown className="w-3 h-3 inline mx-1" /> above to add your video reply.
              </p>
            )}
          </div>
        </div>

        {/* Debate playlist sidebar */}
        <div className="lg:col-span-4 space-y-4">
          {/* Where do people stand? meter */}
          <div className="border border-border rounded-2xl bg-card p-5" data-testid="debate-meter">
            <div className="font-serif text-lg mb-3">Where do people stand?</div>
            {totalCount === 0 ? (
              <div className="text-sm text-muted-foreground py-2">No replies yet — be the first to weigh in.</div>
            ) : (
              <>
                <div className="w-full h-2.5 rounded-full overflow-hidden bg-muted flex mb-3">
                  <div
                    className="h-full bg-[hsl(var(--support))] transition-all duration-500"
                    style={{ width: `${supportPct}%` }}
                    data-testid="meter-support-bar"
                  />
                  <div
                    className="h-full bg-[hsl(var(--oppose))] transition-all duration-500"
                    style={{ width: `${opposePct}%` }}
                    data-testid="meter-oppose-bar"
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <div className="text-[hsl(var(--support))] font-semibold">
                    Support · {supportCount} <span className="text-muted-foreground font-normal">({supportPct}%)</span>
                  </div>
                  <div className="text-[hsl(var(--oppose))] font-semibold">
                    Oppose · {opposeCount} <span className="text-muted-foreground font-normal">({opposePct}%)</span>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Reply list with tabs */}
          <div className="border border-border rounded-2xl bg-card overflow-hidden">
            <div className="grid grid-cols-2 border-b border-border">
              <button
                onClick={() => setTab("support")}
                data-testid="tab-support"
                className={`py-3.5 text-sm font-semibold tracking-wider transition-colors ${
                  tab === "support"
                    ? "bg-[hsl(var(--support-soft))] text-[hsl(var(--support))] border-t-[3px] border-[hsl(var(--support))]"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                SUPPORT ({supportReplies.length})
              </button>
              <button
                onClick={() => setTab("oppose")}
                data-testid="tab-oppose"
                className={`py-3.5 text-sm font-semibold tracking-wider transition-colors ${
                  tab === "oppose"
                    ? "bg-[hsl(var(--oppose-soft))] text-[hsl(var(--oppose))] border-t-[3px] border-[hsl(var(--oppose))]"
                    : "text-muted-foreground hover:bg-muted"
                }`}
              >
                OPPOSE ({opposeReplies.length})
              </button>
            </div>

            <div className="max-h-[560px] overflow-y-auto scrollbar-thin divide-y divide-border">
              {(tab === "support" ? supportReplies : opposeReplies).length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No {tab} replies yet
                </div>
              ) : (
                (tab === "support" ? supportReplies : opposeReplies).map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setSelectedReply(r)}
                    data-testid={`reply-item-${r.id}`}
                    className={`flex gap-3 p-3.5 w-full text-left hover:bg-muted transition-colors ${
                      selectedReply?.id === r.id ? "bg-muted" : ""
                    } border-l-[3px] ${r.position === "support" ? "border-l-[hsl(var(--support))]" : "border-l-[hsl(var(--oppose))]"}`}
                  >
                    <div className="w-20 h-14 rounded-md bg-black flex-shrink-0 flex items-center justify-center border border-border overflow-hidden">
                      {r.thumbnail_url ? (
                        <img src={fileUrl(r.thumbnail_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <Play className="w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm line-clamp-2">{r.text || `Reply from @${r.author?.username}`}</div>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        @{r.author?.username}
                        {r.author?.verification_status === "verified" && (
                          <BadgeCheck className="w-3 h-3 fill-[hsl(var(--primary))] text-white" />
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>

            {selectedReply && (
              <div className="p-3 border-t border-border">
                <Button size="sm" variant="outline" onClick={() => setSelectedReply(null)} className="w-full rounded-full text-xs font-semibold" data-testid="back-to-main">
                  Back to main video
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
